// src/api/services/TicketService.js
import axios from "../axios";
import { showToast } from "../../components/ToastNotifier";

const TICKET_API_URL = "/tickets";
const ATTACHMENT_API_URL = "/attachments";

const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || defaultMessage;
  showToast(errorMessage, "error");
  throw new Error(errorMessage);
};

export const getTicketsPaginated = async (params = {}) => {
  try {
    const response = await axios.get('/tickets/paginated', { params });
    return response.data;
  } catch (error) {
    return error.response?.data?.message || "Failed to load tickets";
  }
}

export const getAllTickets = async () => {
  try {
    const response = await axios.get(TICKET_API_URL);
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to load tickets");
  }
};

export const getTicketById = async (id) => {
  try {
    const response = await axios.get(`${TICKET_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to load ticket details");
  }
};

export const createTicket = async (ticketData) => {
  try {
    const isFormData = ticketData instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    } : {};

    const response = await axios.post(TICKET_API_URL, ticketData, config);
    showToast("Ticket created successfully", "success");
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to create ticket");
  }
};

// api/services/TicketService.js
export const createReport = async (ticketId, formData) => {
  try {
    const response = await axios.post(`/api/tickets/${ticketId}/worker-report`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create a report";
  }
};

export const updateTicket = async (id, ticketData) => {
  try {
    const response = await axios.patch(`${TICKET_API_URL}/${id}`, ticketData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update ticket";
  }
};

export const deleteTicket = async (id) => {
  try {
    const response = await axios.delete(`${TICKET_API_URL}/${id}`);
    showToast("Ticket deleted successfully", "success");
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to delete ticket");
  }
};

export const getAttachmentById = async (id) => {
  try {
    const response = await axios.get(`${ATTACHMENT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to load attachment");
  }
};

export const addAttachment = async (ticketId, file, note = '') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ticketId', ticketId);
    if (note) formData.append('note', note);

    const response = await axios.post(ATTACHMENT_API_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    showToast("Attachment added successfully", "success");
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to add attachment");
  }
};

export const deleteAttachment = async (id) => {
  try {
    const response = await axios.delete(`${ATTACHMENT_API_URL}/${id}`);
    showToast("Attachment deleted successfully", "success");
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to delete attachment");
  }
};

export const prepareTicketFormData = (ticketData, files = []) => {
  const formData = new FormData();

  // Append all ticket data fields
  Object.entries(ticketData).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });

  // Append files
  files.forEach(file => {
    formData.append('file', file);
  });

  return formData;
};


export const getUsersByRole = async (role) => {
  const response = await axios.get(`/users/roles/${role}`); // <-- roles not role
  return response.data;
};

// Pending tickets endpoints
export const getPendingTickets = async () => {
  try {
    const response = await axios.get('tickets/pending');
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to load pending tickets";
  }
}

export const getPendingTicketById = async (id) => {
  try {
    const response = await axios.get(`tickets/pending/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to load pending tickets";
  }
}

export const getTicketsByCompany = async (companyId) => {
  try {
    const response = await axios.get(`tickets/company/${companyId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to load tickets";
  }
}

export const getReportById = async (id) => {
  try {
    const response = await axios.get(`tickets/${id}/worker-report`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to load report";
  }
}

export const updateReportById = async (id, data) => {
  try {
    const response = await axios.patch(`tickets/${id}/worker-report`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to update the report";
  }
}

export const deleteReport = async (id) => {
  try {
    const response = await axios.delete(`tickets/${id}/worker-report`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Faild to update the report";
  }
}

export const downloadTicketImage = async (ticketId) => {
  try {
    console.log(`Downloading ticket image for ticket ID: ${ticketId}`);
    const response = await axios.get(`${TICKET_API_URL}/${ticketId}/image/download`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/octet-stream, image/*'
      }
    });
    
    console.log('Response received:', {
      status: response.status,
      statusText: response.statusText,
      dataType: typeof response.data,
      dataSize: response.data?.size,
      contentType: response.headers['content-type']
    });
    
    // Validate response
    if (!response.data) {
      throw new Error("No data received from server");
    }
    
    if (response.data.size === 0) {
      throw new Error("Empty file received from server");
    }
    
    return response.data;
  } catch (error) {
    console.error('Error downloading ticket image:', {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    
    // Try to extract error message from blob response if it's an error JSON
    let errorMessage = "Failed to download ticket image";
    if (error.response?.data) {
      try {
        // If the response is a blob but contains JSON error, convert it
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            errorMessage = jsonError.message || errorMessage;
          } catch (parseError) {
            // If it's not JSON, use status text or default message
            errorMessage = error.response.statusText || errorMessage;
          }
        } else {
          errorMessage = error.response.data.message || errorMessage;
        }
      } catch (e) {
        // If parsing fails, use default message
        errorMessage = error.response?.statusText || error.message || errorMessage;
      }
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    throw new Error(errorMessage);
  }
}

export const downloadTicketAttachment = async (ticketId) => {
  try {
    const response = await axios.get(`${TICKET_API_URL}/${ticketId}/attachment/download`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to download ticket attachment";
  }
}